/**
 * O confirmador do cartão `cliente/segmentacao-carteira` — spec-005, [T032] / US-6.
 *
 * ## O que ele resolve
 *
 * Marcar uma exceção no cartão precisa RE-EXECUTAR a tool com a lista completa
 * de exceções — e mais nada: nenhum `PATCH`, nenhum `POST` de cadastro, nenhuma
 * gravação (regra 4 do `contracts/ui-components.md`). A re-execução é uma FRASE
 * ao modelo, e é este arquivo que a monta.
 *
 * ## Por que não bastava o canal genérico
 *
 * O `respond` do contrato manda ao modelo um "Confirmo" genérico, que aqui não
 * diz nada: o modelo precisa saber QUAIS códigos ficam de fora da régua. Sem a
 * frase própria, ele reexecutaria a tool sem exceção nenhuma — e o consultor
 * veria os clientes que acabou de manter voltarem para o grupo que sai.
 *
 * ★ A ação é `confirmar` (não `custom`) porque `view-chat.responderCartao`
 * trata só `confirmar`/`cancelar` — `custom` é descartado no guard, e o clique
 * morreria no caminho sem erro nenhum.
 *
 * ## ★ NENHUMA requisição sai daqui
 *
 * Diferente do confirmador da simulação do Fee Fixo (que precisa depositar os
 * resultados calculados no navegador antes da frase), aqui não há passo local:
 * o servidor recalcula tudo. Este arquivo não importa camada de rede, e há
 * teste que lê o fonte para travar isso.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { registrarConfirmador } from '@modules/aawz-intelligence-v2/ui-registry/confirmadores'
import { CHAVES } from '@modules/clients/ia/registry'

const ACAO_CONFIRMAR = 'confirmar'

/** O que o consultor lê na conversa, no lugar do "Confirmo" genérico. */
const EXIBIR_COM_EXCECAO = 'Refazer a segmentação mantendo os clientes que marquei.'
const EXIBIR_SEM_EXCECAO = 'Refazer a segmentação sem nenhuma exceção.'

/**
 * O passo local da segmentação — que aqui é só a frase.
 *
 * @param {Object}  ctx
 * @param {string}  ctx.acao   `confirmar` | `cancelar`.
 * @param {?Object} ctx.dados  `{ excecoes: string[] }`, emitido pelo cartão.
 * @returns {?{enviar?: Object}} `null` devolve o cartão ao caminho genérico.
 */
export function confirmarSegmentacaoDoChat({ acao, dados } = {}) {
  if (acao !== ACAO_CONFIRMAR) return null

  const excecoes = (dados && dados.excecoes) || []
  if (!Array.isArray(excecoes)) return null

  if (!excecoes.length) {
    return {
      enviar: {
        message:
          'Refaça a segmentação da minha carteira com a mesma régua, sem ' +
          'nenhuma exceção (removi todas as que eu tinha marcado).',
        exibir: EXIBIR_SEM_EXCECAO,
      },
    }
  }

  // A lista COMPLETA, sempre: mandar só o código recém-clicado faria a
  // re-execução apagar as exceções dos turnos anteriores.
  return {
    enviar: {
      message:
        'Refaça a segmentação da minha carteira com a mesma régua, mantendo ' +
        'estes clientes mesmo abaixo dela (exceções): ' +
        excecoes.join(', ') + '.',
      exibir: EXIBIR_COM_EXCECAO,
    },
  }
}

/**
 * Publica o confirmador. Chamado no boot do módulo (`clients/index.js`), pelo
 * mesmo motivo do `registrar(registryClients)` ao lado: o cartão pode chegar em
 * QUALQUER conversa, e registrar numa view faria o mesmo clique funcionar numa
 * tela e morrer na outra.
 */
export function registrarConfirmadorDeSegmentacao() {
  return registrarConfirmador(CHAVES.SEGMENTACAO, confirmarSegmentacaoDoChat)
}
